// src/Stores/InstructorStore/InstructorLearningsStore.ts
import { create } from 'zustand';
import {
  getMyCourses,
  getWishlistCourses,
  getBrowseCourses,
  getAllActiveCourses,
  getCourseDetails,
  getLessonDetails,
  saveCourse,
  unsaveCourse,
  enrollInCourse,
  markLessonComplete,
  Course,
  CourseDetails,
  LessonData,
} from '@/services/InstructorService/InstructorLearningsService';

interface InstructorLearningsState {
  // Data
  myCourses: Course[] | null;
  wishlistCourses: Course[] | null;
  browseCourses: Course[] | null;
  allActiveCourses: Course[] | null;
  courseDetails: CourseDetails | null;
  lessonDetails: LessonData | null;
  savedCourseIds: Set<number>;

  // Loading states
  loadingMyCourses: boolean;
  loadingWishlist: boolean;
  loadingBrowse: boolean;
  loadingAllActive: boolean;
  loadingCourseDetails: boolean;
  loadingLessonDetails: boolean;
  loadingSavedStatus: boolean;

  // Error states
  errorMyCourses: string | null;
  errorWishlist: string | null;
  errorBrowse: string | null;
  errorAllActive: string | null;
  errorCourseDetails: string | null;
  errorLessonDetails: string | null;

  // Actions
  fetchMyCourses: () => Promise<void>;
  fetchWishlistCourses: () => Promise<void>;
  fetchBrowseCourses: () => Promise<void>;
  fetchAllActiveCourses: () => Promise<void>;
  fetchCourseDetails: (courseId: number) => Promise<void>;
  fetchLessonDetails: (courseId: number, lessonId: number) => Promise<void>;
  fetchSavedCourseIds: () => Promise<void>;
  toggleSaveCourse: (courseId: number) => Promise<void>;
  enrollInCourse: (courseId: number) => Promise<void>;
  markLessonComplete: (lessonId: number, courseId: number) => Promise<void>;
}

export const useInstructorLearningsStore = create<InstructorLearningsState>((set, get) => ({
  // Initial state
  myCourses: null,
  wishlistCourses: null,
  browseCourses: null,
  allActiveCourses: null,
  courseDetails: null,
  lessonDetails: null,
  savedCourseIds: new Set<number>(),

  loadingMyCourses: false,
  loadingWishlist: false,
  loadingBrowse: false,
  loadingAllActive: false,
  loadingCourseDetails: false,
  loadingLessonDetails: false,
  loadingSavedStatus: false,

  errorMyCourses: null,
  errorWishlist: null,
  errorBrowse: null,
  errorAllActive: null,
  errorCourseDetails: null,
  errorLessonDetails: null,

  // Fetch My Courses
  fetchMyCourses: async () => {
    set({ loadingMyCourses: true, errorMyCourses: null });

    try {
      const res = await getMyCourses();

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load my courses');
      }

      set({
        myCourses: res.data ?? [],
        loadingMyCourses: false,
      });
    } catch (err: any) {
      set({
        errorMyCourses: err.message ?? 'Something went wrong',
        loadingMyCourses: false,
      });
    }
  },

  // Fetch Wishlist Courses
  fetchWishlistCourses: async () => {
    set({ loadingWishlist: true, errorWishlist: null });

    try {
      const res = await getWishlistCourses();

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load wishlist courses');
      }

      set({
        wishlistCourses: res.data ?? [],
        loadingWishlist: false,
      });
    } catch (err: any) {
      set({
        errorWishlist: err.message ?? 'Something went wrong',
        loadingWishlist: false,
      });
    }
  },

  // Fetch Browse Courses
  fetchBrowseCourses: async () => {
    set({ loadingBrowse: true, errorBrowse: null });

    try {
      const res = await getBrowseCourses();

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load browse courses');
      }

      set({
        browseCourses: res.data ?? [],
        loadingBrowse: false,
      });
    } catch (err: any) {
      set({
        errorBrowse: err.message ?? 'Something went wrong',
        loadingBrowse: false,
      });
    }
  },

  // Fetch All Active Courses
  fetchAllActiveCourses: async () => {
    set({ loadingAllActive: true, errorAllActive: null });

    try {
      const res = await getAllActiveCourses();

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load all active courses');
      }

      set({
        allActiveCourses: res.data ?? [],
        loadingAllActive: false,
      });
    } catch (err: any) {
      set({
        errorAllActive: err.message ?? 'Something went wrong',
        loadingAllActive: false,
      });
    }
  },

  // Fetch Course Details
  fetchCourseDetails: async (courseId: number) => {
    console.log('[InstructorLearningsStore] fetchCourseDetails called with courseId:', courseId);
    set({ loadingCourseDetails: true, errorCourseDetails: null });

    try {
      console.log('[InstructorLearningsStore] Calling API getCourseDetails...');
      const res = await getCourseDetails(courseId);
      console.log('[InstructorLearningsStore] API response:', res);

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load course details');
      }

      console.log('[InstructorLearningsStore] Course details loaded successfully:', res.data);
      set({
        courseDetails: res.data ?? null,
        loadingCourseDetails: false,
      });
    } catch (err: any) {
      console.error('[InstructorLearningsStore] Error fetching course details:', err);
      set({
        errorCourseDetails: err.message ?? 'Something went wrong',
        loadingCourseDetails: false,
      });
    }
  },

  // Fetch Saved Course IDs
  // Fetches all saved courses and extracts their IDs to populate savedCourseIds Set
  fetchSavedCourseIds: async () => {
    set({ loadingSavedStatus: true });

    try {
      const res = await getWishlistCourses();

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load saved courses');
      }

      const ids = new Set((res.data ?? []).map(course => course.id));
      set({
        savedCourseIds: ids,
        loadingSavedStatus: false,
      });
    } catch (err: any) {
      set({
        loadingSavedStatus: false,
      });
      console.error('Failed to fetch saved course IDs:', err.message);
    }
  },

  // Toggle Save Course (Optimistic Update)
  // Immediately updates UI, then calls API, rolls back on error
  toggleSaveCourse: async (courseId: number) => {
    const { savedCourseIds, wishlistCourses } = get();
    const wasSaved = savedCourseIds.has(courseId);

    // Create new set for optimistic update
    const newSavedIds = new Set(savedCourseIds);
    if (wasSaved) {
      newSavedIds.delete(courseId);
    } else {
      newSavedIds.add(courseId);
    }

    // Optimistic update - immediately update UI
    set({ savedCourseIds: newSavedIds });

    try {
      // Call API
      const res = wasSaved
        ? await unsaveCourse(courseId)
        : await saveCourse(courseId);

      if (!res.success) {
        // Rollback on error
        set({ savedCourseIds });
        throw new Error(res.error?.message ?? 'Failed to toggle save status');
      }

      // Refresh wishlist if currently viewing it
      if (wishlistCourses !== null) {
        const listRes = await getWishlistCourses();
        if (listRes.success) {
          set({ wishlistCourses: listRes.data ?? [] });
        }
      }
    } catch (err: any) {
      // Rollback on error
      set({ savedCourseIds });
      throw err;
    }
  },

  // Enroll in Course
  // Enrolls instructor and refreshes my courses list
  enrollInCourse: async (courseId: number) => {
    set({ loadingBrowse: true, errorBrowse: null });

    try {
      const res = await enrollInCourse(courseId);

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to enroll in course');
      }

      // Refresh my courses to include newly enrolled course
      await get().fetchMyCourses();

      // Also refresh browse courses to update is_enrolled status
      // This will cause the enrolled course to disappear from the browse list
      await get().fetchBrowseCourses();

      set({ loadingBrowse: false });
    } catch (err: any) {
      set({
        errorBrowse: err.message ?? 'Something went wrong',
        loadingBrowse: false,
      });
      throw err;
    }
  },

  // Fetch Lesson Details
  // Fetches detailed information about a specific lesson
  fetchLessonDetails: async (courseId: number, lessonId: number) => {
    console.log('[InstructorLearningsStore] fetchLessonDetails called with courseId:', courseId, 'lessonId:', lessonId);
    set({ loadingLessonDetails: true, errorLessonDetails: null });

    try {
      console.log('[InstructorLearningsStore] Calling API getLessonDetails...');
      const res = await getLessonDetails(courseId, lessonId);
      console.log('[InstructorLearningsStore] API response:', res);

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load lesson details');
      }

      console.log('[InstructorLearningsStore] Lesson details loaded successfully:', res.data);
      set({
        lessonDetails: res.data ?? null,
        loadingLessonDetails: false,
      });
    } catch (err: any) {
      console.error('[InstructorLearningsStore] Error fetching lesson details:', err);
      set({
        errorLessonDetails: err.message ?? 'Something went wrong',
        loadingLessonDetails: false,
      });
    }
  },

  // Mark Lesson Complete
  // Marks a lesson as completed by calling the API
  markLessonComplete: async (lessonId: number, courseId: number) => {
    try {
      const res = await markLessonComplete(lessonId, courseId);

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to mark lesson complete');
      }

      // Optionally refresh course details to update lesson completion status
      // This is useful to see the updated lesson state in the UI
      const { courseDetails } = get();
      if (courseDetails && courseDetails.id === courseId) {
        await get().fetchCourseDetails(courseId);
      }
    } catch (err: any) {
      console.error('Failed to mark lesson complete:', err.message);
      throw err;
    }
  },
}));
